import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import AppBar from "../../common/AppBar";
import AppTable from "../../common/AppTable/AppTable";
import {
  deleteSuggestionRule,
  getAllSuggestionRules,
  setLimit,
  setOffset,
  exportSuggestionRulesCSV,
  createSuggestionRule,
  updateSuggestionRule,
  uploadSuggestionRulesCSV,
  getCriteriaFormats,
} from "../../redux/suggestionSlice";
import { getAllTypes } from "../../redux/typeSlice";
import { showSnackbar } from "../../redux/snackbarSlice";
import { scrollToTop, uploadJsonToS3 } from "../../util/AppUtils";
import DeleteConfirmation from "../../common/Confirmations/DeleteConfirmation";
import AddBtn from "../../common/AppSpecificConpnents/AddBtn";
import UploadViaCsvBtn from "../../common/AppSpecificConpnents/UploadViaCsvBtn";
import ExportCsvBtn from "../../common/AppSpecificConpnents/ExportCsvBtn";
import { TableActionGroup } from "../../common/Buttons";
import TypesPopup from "../../common/AppSpecificConpnents/Types/TypesPopup";
import { getAuditTrailData } from "../../util/getAuditTrailData";
import Loader from "../../common/Loader";
import ImportCsvSuccessPopup from "../../common/ImportCsvSuccessPopup";
import PartsPopup from "../../common/AppSpecificConpnents/PartsTable/PartsPopup";
import CustomSelect from "../ApplicationSetup/components/CustomSelect";
import "../ApplicationSetup/components/CustomSelect.css";

// Criteria formats will be fetched from API

const columnNames = [
  {
    headName: "Suggestion",
    field: "rule_name",
    sort: false,
    filter: true,
    width: "20%",
  },
  {
    headName: "Applicable Types",
    field: "type_name",
    sort: false,
    filter: true,
    width: "20%",
  },
  {
    headName: "Criteria",
    field: "criteria",
    sort: false,
    filter: true,
    width: "20%",
  },
  {
    headName: "Criteria Format",
    field: "criteria_format_name",
    sort: false,
    filter: true,
    width: "15%",
  },
  {
    headName: "Matches",
    field: "matches",
    sort: false,
    filter: false,
    textCenter: true,
  },
  {
    headName: "Action",
    field: "action",
    sort: false,

    isAction: true,
  },
];

const SuggestionRules = () => {
  const dispatch = useDispatch();
  const { suggestions, loading, error, pagination, criteriaFormats } =
    useSelector((state) => state.suggestion);
  const { types } = useSelector((state) => state.type);

  const [limit, setLocalLimit] = useState(pagination?.limit || 10);
  const [offset, setLocalOffset] = useState(pagination?.offset || 0);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentApiQuery, setCurrentApiQuery] = useState("");
  const tableRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showLoader, setShowLoader] = useState(false);
  const [importedInfo, setImportData] = useState(null);

  // Inline editing state
  const [newRow, setNewRow] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedRowData, setEditedRowData] = useState({});
  const [newRowData, setNewRowData] = useState({
    rule_name: "",
    type_ids: [],
    criteria: "",
    criteria_format_ref: "",
    matches: 0,
  });
  const [typesModalOpen, setTypesModalOpen] = useState(false);
  const [addingTypeToRow, setAddingTypeToRow] = useState(null); // Track which row is adding a type
  const [partsModalOpen, setPartsModalOpen] = useState(false);
  const [selectedRuleForMatches, setSelectedRuleForMatches] = useState(null);
  const [partsQueryParams, setPartsQueryParams] = useState("");
  const [expandedTypesRows, setExpandedTypesRows] = useState(new Set()); // Track which rows have expanded types view
  const [extraTypeInfo, setExtraTypeInfo] = useState({});

  useEffect(() => {
    dispatch(getAllTypes({ limit: 100, offset: 0 }));
    dispatch(getAllSuggestionRules({ limit, offset }));
    dispatch(getCriteriaFormats());
  }, [dispatch]);

  // AppTable integration helpers
  const reduxSlice = {
    setLimit: (value) => dispatch(setLimit(value)),
    setOffset: (value) => dispatch(setOffset(value)),
  };

  // Helper function to cancel editing
  const cancelEditing = () => {
    if (editingRowId !== null) {
      setEditingRowId(null);
      setEditedRowData({});
    }
    if (newRow !== null) {
      setNewRow(null);
      setNewRowData({
        rule_name: "",
        type_ids: [],
        criteria: "",
        criteria_format_ref: "",
      });
    }
  };

  const handleFilterSort = (queryString) => {
    if (queryString !== currentApiQuery) {
      // Cancel editing when search/filter changes
      cancelEditing();
      setSearchQuery(queryString);
      setCurrentApiQuery(queryString);
      // If queryString is a full query string, don't pass separate limit/offset
      if (queryString && queryString.includes("=")) {
        dispatch(getAllSuggestionRules({ searchQuery: queryString }));
      } else {
        dispatch(
          getAllSuggestionRules({ searchQuery: queryString, limit, offset })
        );
      }
    }
  };

  useEffect(() => {
    // Pagination changes
    // If currentApiQuery is a full query string, update limit/offset in it
    if (currentApiQuery && currentApiQuery.includes("=")) {
      const params = new URLSearchParams(currentApiQuery);
      params.set("limit", limit);
      params.set("offset", offset);
      const updatedQuery = params.toString();
      dispatch(getAllSuggestionRules({ searchQuery: updatedQuery }));
    } else {
      // Otherwise, pass search query with separate limit/offset
      dispatch(
        getAllSuggestionRules({ searchQuery: currentApiQuery, limit, offset })
      );
    }
  }, [limit, offset, dispatch, currentApiQuery]);

  const snackbar = (message, type) => {
    dispatch(showSnackbar({ type: type, message: message }));
  };

  const handleAddRow = () => {
    // Check if a row is already being edited
    if (editingRowId !== null) {
      snackbar(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      return;
    }

    // Check if a new row is already being added
    if (newRow !== null) {
      snackbar(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      return;
    }

    setNewRow({});
    setNewRowData({
      rule_name: "",
      type_ids: [],
      criteria: "",
      criteria_format_ref: "",
    });
    scrollToTop(tableRef);
  };

  const handleSaveNewRow = async () => {
    if (!newRowData.rule_name.trim() || !newRowData.criteria.trim()) {
      snackbar("Suggestion and Criteria are required!", "error");
      return;
    }
    if (!newRowData.criteria_format_ref) {
      snackbar("Criteria Format is required!", "error");
      return;
    }

    try {
      setShowLoader(true);
      const criteriaFormatText = getCriteriaFormatText(
        newRowData.criteria_format_ref
      );
      const typeNames = getTypeNames(newRowData.type_ids);

      const auditTrailData = await getAuditTrailData({
        action: "CREATE_SUGGESTION_RULE",
        comment: `Created suggestion rule: ${newRowData.rule_name}`,
        version_content: {
          action_to_show: "Created a Suggestion Rule",
          rule_name: newRowData.rule_name.trim(),
          type_names: typeNames,
          criteria: newRowData.criteria.trim(),
          criteria_format_text: criteriaFormatText,
        },
      });

      auditTrailData.new_content_snapshot = await uploadJsonToS3(
        auditTrailData.version_content, 
        "suggestion_rules", 
        "CREATED", 
        true
      )

      auditTrailData.version_content = { action_to_show: "Created a Suggestion Rule"}

      const payload = {
        rule_name: newRowData.rule_name.trim(),
        type_ids: newRowData.type_ids,
        criteria: newRowData.criteria.trim(),
        criteria_format_ref: newRowData.criteria_format_ref,
        audit_trail_data: auditTrailData,
      };

      await dispatch(createSuggestionRule(payload)).unwrap();
      setNewRow(null);
      setNewRowData({
        rule_name: "",
        type_ids: [],
        criteria: "",
        criteria_format_ref: "",
      });
      snackbar("Suggestion rule created successfully!", "success");
      dispatch(
        getAllSuggestionRules({ searchQuery: currentApiQuery, limit, offset })
      );
    } catch (error) {
      // Extract error message from various possible locations
      let errorMessage = "Failed to create suggestion rule.";

      if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object") {
        if (error.success === false && error.message) {
          errorMessage = error.message;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        }
      }

      if (errorMessage === "Failed to create suggestion rule.") {
        errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          error?.payload?.message ||
          error?.payload?.error ||
          (error?.payload &&
            typeof error.payload === "object" &&
            error.payload.message) ||
          (error?.payload &&
            typeof error.payload === "object" &&
            error.payload.error) ||
          "Failed to create suggestion rule.";
      }

      snackbar(errorMessage, "error");
    } finally {
      setShowLoader(false);
    }
  };

  const handleEdit = (rule) => {
    // Check if a row is already being edited
    if (editingRowId !== null) {
      snackbar(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      // Return early without modifying any state to preserve the current editing state
      return;
    }

    // Check if a new row is already being added
    if (newRow !== null) {
      snackbar(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      // Return early without modifying any state to preserve the current editing state
      return;
    }

    const criteriaFormatRef = getCriteriaFormatRef(rule);
    setEditingRowId(rule.rule_id || rule.id);
    setEditedRowData({
      rule_name: rule.rule_name || "",
      type_ids: rule.type_ids || rule.mapped_types?.map((t) => t.type_id) || [],
      criteria: rule.criteria || "",
      criteria_format_ref: criteriaFormatRef,
    });
  };

  const handleSaveEdit = async (id) => {
    if (!editedRowData.rule_name.trim() || !editedRowData.criteria.trim()) {
      snackbar("Suggestion and Criteria are required!", "error");
      return;
    }
    if (!editedRowData.criteria_format_ref) {
      snackbar("Criteria Format is required!", "error");
      return;
    }

    try {
      setShowLoader(true);
      const ruleToUpdate = suggestions.find((r) => (r.rule_id || r.id) === id);
      const criteriaFormatText = getCriteriaFormatText(
        editedRowData.criteria_format_ref
      );
      const typeNames = getTypeNames(
        editedRowData.type_ids || [],
        ruleToUpdate?.mapped_types
      );

      const auditTrailData = await getAuditTrailData({
        action: "UPDATE_SUGGESTION_RULE",
        comment: `Updated suggestion rule: ${editedRowData.rule_name}`,
        parent_version: ruleToUpdate?.version_id_ref || null,
        version_content: {
          action_to_show: "Updated a Suggestion Rule",
          rule_name: editedRowData.rule_name.trim(),
          type_names: typeNames,
          criteria: editedRowData.criteria.trim(),
          criteria_format_text: criteriaFormatText,
        },
      });

      auditTrailData.new_content_snapshot = await uploadJsonToS3(
        auditTrailData.version_content,
        "suggestion_rules",
        "UPDATED",
        true
      )

      auditTrailData.version_content = { action_to_show: "Updated a Suggestion Rule"}

      const payload = {
        rule_name: editedRowData.rule_name.trim(),
        type_ids: editedRowData.type_ids,
        criteria: editedRowData.criteria.trim(),
        criteria_format_ref: editedRowData.criteria_format_ref,
        audit_trail_data: auditTrailData,
      };

      await dispatch(updateSuggestionRule({ id, data: payload })).unwrap();
      setEditingRowId(null);
      setEditedRowData({});
      snackbar("Suggestion rule updated successfully!", "success");
      dispatch(
        getAllSuggestionRules({ searchQuery: currentApiQuery, limit, offset })
      );
    } catch (error) {
      // Extract error message from various possible locations
      let errorMessage = "Failed to update suggestion rule.";

      if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object") {
        if (error.success === false && error.message) {
          errorMessage = error.message;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        }
      }

      if (errorMessage === "Failed to update suggestion rule.") {
        errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          error?.payload?.message ||
          error?.payload?.error ||
          (error?.payload &&
            typeof error.payload === "object" &&
            error.payload.message) ||
          (error?.payload &&
            typeof error.payload === "object" &&
            error.payload.error) ||
          "Failed to update suggestion rule.";
      }

      snackbar(errorMessage, "error");
    } finally {
      setShowLoader(false);
    }
  };

  const handleDelete = (id) => {
    DeleteConfirmation(async () => {
      try {
        setShowLoader(true);
        await dispatch(deleteSuggestionRule(id)).unwrap();
        snackbar("Suggestion rule deleted successfully!", "success");
        dispatch(
          getAllSuggestionRules({
            searchQuery: currentApiQuery,
            limit,
            offset,
          })
        );
      } catch (error) {
        // Extract error message from various possible locations
        let errorMessage = "Failed to delete suggestion rule.";

        if (typeof error === "string") {
          errorMessage = error;
        } else if (error && typeof error === "object") {
          if (error.success === false && error.message) {
            errorMessage = error.message;
          } else if (error.message) {
            errorMessage = error.message;
          } else if (error.error) {
            errorMessage = error.error;
          }
        }

        if (errorMessage === "Failed to delete suggestion rule.") {
          errorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            error?.payload?.message ||
            error?.payload?.error ||
            (error?.payload &&
              typeof error.payload === "object" &&
              error.payload.message) ||
            (error?.payload &&
              typeof error.payload === "object" &&
              error.payload.error) ||
            "Failed to delete suggestion rule.";
        }

        snackbar(errorMessage, "error");
      } finally {
        setShowLoader(false);
      }
    }, "Are you sure you want to delete this suggestion rule?");
  };

  const REQUIRED_CSV_HEADERS = [
    "Suggestion",
    "Criteria",
    "Criteria Format",
  ];

  const parseCsvHeaderLine = (headerLine = "") => {
    // Parse CSV header while handling quoted values.
    const headers = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < headerLine.length; i++) {
      const char = headerLine[i];
      const next = headerLine[i + 1];

      if (char === '"') {
        if (inQuotes && next === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        headers.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }

    headers.push(current.trim());
    return headers.map((h) => h.replace(/^"|"$/g, "").trim());
  };

  const getMissingCsvHeadersFromFile = async (file) => {
    try {
      const content = await file.text();
      const firstLine = content.split(/\r?\n/)[0] || "";
      const uploadedHeaders = parseCsvHeaderLine(firstLine);
      const normalizedHeaderSet = new Set(
        uploadedHeaders.map((header) => header.toLowerCase())
      );

      return REQUIRED_CSV_HEADERS.filter(
        (header) => !normalizedHeaderSet.has(header.toLowerCase())
      );
    } catch (error) {
      return [];
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    e.target.value = "";

    try {
      setShowLoader(true);
      const fallbackMissingHeaders = await getMissingCsvHeadersFromFile(file);
      const auditTrailData = await getAuditTrailData({
        action: "IMPORT_SUGGESTION_RULES_DATA",
        comment: `Imported suggestion rules via CSV: ${file.name}`,
        version_content: {
          action_to_show: "Imported Suggestion Rules via CSV",
          file_name: file.name,
        },
      });

      const response = await dispatch(
        uploadSuggestionRulesCSV({ csvFile: file, auditTrailData })
      );
      setShowLoader(false);

      let payload = response.payload;
      let importData;
      
      // Handle both fulfilled and rejected responses
      if (uploadSuggestionRulesCSV.fulfilled.match(response)) {
        // Fulfilled case - success response
        importData = {
          ...payload,
          csv: payload?.csv,
          missingHeaders:
            payload?.missingHeaders ||
            payload?.missing_headers ||
            fallbackMissingHeaders,
        };
        
        // Show success snackbar and refresh data if import was successful
        if (payload?.success) {
          snackbar(payload?.message || "CSV imported successfully!", "success");
          dispatch(
            getAllSuggestionRules({
              searchQuery: currentApiQuery,
              limit,
              offset,
            })
          );
        }
      } else if (uploadSuggestionRulesCSV.rejected.match(response)) {
        // Rejected case - but the API might still return data with csv field
        // The actual API response is in the error payload
        importData = {
          ...payload,
          success: false,
          csv: payload?.csv,
          missingHeaders:
            payload?.missingHeaders ||
            payload?.missing_headers ||
            fallbackMissingHeaders,
        };
      }
      
      setImportData(importData);
    } catch (err) {
      setShowLoader(false);
      // Extract error message
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        err?.payload?.message ||
        "Operation failed. Please try again.";
      snackbar(errorMessage, "error");
    }
  };

  const handleExportCSV = () => {
    setShowLoader(true);
    dispatch(exportSuggestionRulesCSV()).then((res) => {
      setShowLoader(false);
      if (exportSuggestionRulesCSV.fulfilled.match(res)) {
        snackbar("CSV export completed!", "success");
      } else {
        snackbar("Error exporting CSV: " + res.payload, "error");
      }
    });
  };

  const handleSelectType = (type) => {
    // Handle both formats: {type_id, name} or {value, label}
    const typeId = type.type_id || type.value;
    const typeName = type.name || type.label;

    if (typeId && typeName) {
      setExtraTypeInfo((prev) => ({ ...prev, [typeId]: typeName }));
    }

    if (!typeId) {
      snackbar("Invalid type selected", "error");
      return;
    }

    if (addingTypeToRow === "new") {
      // Adding to new row
      const typeIds = newRowData.type_ids || [];
      if (!typeIds.includes(typeId)) {
        setNewRowData({
          ...newRowData,
          type_ids: [...typeIds, typeId],
        });
      }
    } else if (addingTypeToRow) {
      // Adding to editing row
      const typeIds = editedRowData.type_ids || [];
      if (!typeIds.includes(typeId)) {
        setEditedRowData({
          ...editedRowData,
          type_ids: [...typeIds, typeId],
        });
      }
    }
    // Close modal after selecting a type
    setTypesModalOpen(false);
    setAddingTypeToRow(null);
  };

  const handleRemoveType = (typeId, isNewRow = false) => {
    if (isNewRow) {
      setNewRowData({
        ...newRowData,
        type_ids: (newRowData.type_ids || []).filter((id) => id !== typeId),
      });
    } else {
      setEditedRowData({
        ...editedRowData,
        type_ids: (editedRowData.type_ids || []).filter((id) => id !== typeId),
      });
    }
  };

  const getTypeName = (typeId, mappedTypes = null) => {
    // First, try to get the name from mapped_types if provided
    if (mappedTypes && Array.isArray(mappedTypes)) {
      const mappedType = mappedTypes.find((t) => t.type_id === typeId);
      if (mappedType && mappedType.name) {
        return mappedType.name;
      }
    }
    // Check local cache
    if (extraTypeInfo[typeId]) {
      return extraTypeInfo[typeId];
    }
    // Fallback to Redux state lookup
    const type = types.find((t) => t.type_id === typeId);
    return type ? type.name : "Unknown";
  };

  const getTypeNames = (typeIds, mappedTypes = null) => {
    if (!typeIds || !Array.isArray(typeIds) || typeIds.length === 0) {
      return [];
    }
    return typeIds.map((typeId) => {
      // First, try to get the name from mapped_types if provided
      if (mappedTypes && Array.isArray(mappedTypes)) {
        const mappedType = mappedTypes.find((t) => t.type_id === typeId);
        if (mappedType && mappedType.name) {
          return mappedType.name;
        }
      }
      // Check local cache
      if (extraTypeInfo[typeId]) {
        return extraTypeInfo[typeId];
      }
      // Fallback to Redux state lookup
      const type = types.find((t) => t.type_id === typeId);
      return type ? type.name : "Unknown";
    });
  };

  const getCriteriaFormatText = (criteriaFormatRef) => {
    if (
      !criteriaFormatRef ||
      !criteriaFormats ||
      criteriaFormats.length === 0
    ) {
      return "";
    }
    const criteriaFormat = criteriaFormats.find(
      (format) =>
        (format.criteria_format_id || format.id || format.value) ===
        criteriaFormatRef
    );
    return (
      criteriaFormat?.criteria_format_name ||
      criteriaFormat?.name ||
      criteriaFormat?.label ||
      ""
    );
  };

  const getCriteriaFormatRef = (rule) => {
    // First, try to get criteria_format_ref directly from rule
    if (rule.criteria_format_ref) {
      return rule.criteria_format_ref;
    }
    // If not available, try to find it from criteria_format_name
    if (
      rule.criteria_format_name &&
      criteriaFormats &&
      criteriaFormats.length > 0
    ) {
      const criteriaFormat = criteriaFormats.find(
        (format) =>
          (format.criteria_format_name || format.name || format.label) ===
          rule.criteria_format_name
      );
      if (criteriaFormat) {
        return (
          criteriaFormat.criteria_format_id ||
          criteriaFormat.id ||
          criteriaFormat.value ||
          ""
        );
      }
    }
    return "";
  };

  const renderApplicableTypes = (
    typeIds,
    isNewRow = false,
    isEditing = false,
    rowId = null,
    mappedTypes = null
  ) => {
    // Handle both old format (applicable_types) and new format (type_ids)
    const actualTypeIds = typeIds || [];
    if (!isEditing) {
      // View mode - just show the types as text
      return (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            alignItems: "center",
          }}
        >
          {actualTypeIds && actualTypeIds.length > 0 ? (
            actualTypeIds.map((typeId) => (
              <span
                key={typeId}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "4px 8px",
                  backgroundColor: "#E9ECEF",
                  borderRadius: "4px",
                  fontSize: "12px",
                  color: "#333",
                }}
              >
                {getTypeName(typeId, mappedTypes)}
              </span>
            ))
          ) : (
            <span style={{ color: "#666", fontSize: "13px" }}>-</span>
          )}
        </div>
      );
    }

    // Editing mode - use InputWithPen with chips
    const rowKey = isNewRow ? "new" : rowId;
    const isExpanded = expandedTypesRows.has(rowKey);
    const hasMultipleTypes = actualTypeIds && actualTypeIds.length > 0;

    // Show expand/collapse if more than 2 types (since 2 might fit, but 3+ likely won't)
    const needsExpandCollapse = hasMultipleTypes && actualTypeIds.length > 2;

    const toggleExpand = () => {
      const newExpanded = new Set(expandedTypesRows);
      if (isExpanded) {
        newExpanded.delete(rowKey);
      } else {
        newExpanded.add(rowKey);
      }
      setExpandedTypesRows(newExpanded);
    };

    return (
      <div style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        {/* Collapsed view */}
        {!isExpanded && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #ddd",
              borderRadius: "4px",
              width: "100%",
              maxWidth: "100%",
              background: "#fff",
              minHeight: "25px",
              height: "25px",
              padding: "2px 0 2px 4px",
              flexWrap: "nowrap",
              gap: "2px",
              boxSizing: "border-box",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Chips for selected types - limited view */}
            <div
              style={{
                display: "flex",
                flexWrap: "nowrap",
                gap: "2px",
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                alignItems: "center",
              }}
            >
              {actualTypeIds && actualTypeIds.length > 0 ? (
                <>
                  {actualTypeIds.slice(0, 2).map((typeId) => (
                    <span
                      key={typeId}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "2px",
                        padding: "2px 6px",
                        backgroundColor: "#E9ECEF",
                        borderRadius: "3px",
                        fontSize: "12px",
                        color: "#333",
                        boxSizing: "border-box",
                        height: "19px",
                        lineHeight: "1",
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100px",
                        }}
                      >
                        {getTypeName(typeId, mappedTypes)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveType(typeId, isNewRow)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "2px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#666",
                          marginLeft: "4px",
                          flexShrink: 0,
                          height: "16px",
                          width: "16px",
                          minWidth: "16px",
                        }}
                        title="Remove type"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <line
                            x1="3.5"
                            y1="3.5"
                            x2="10.5"
                            y2="10.5"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                          />
                          <line
                            x1="10.5"
                            y1="3.5"
                            x2="3.5"
                            y2="10.5"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {actualTypeIds.length > 2 && (
                    <span
                      style={{
                        color: "#666",
                        fontSize: "12px",
                        padding: "0 4px",
                        flexShrink: 0,
                      }}
                    >
                      +{actualTypeIds.length - 2} more
                    </span>
                  )}
                </>
              ) : (
                <span
                  style={{
                    color: "#999",
                    fontSize: "13px",
                    paddingTop: "4px",
                    paddingBottom: "6px",
                    lineHeight: "21px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Select types
                </span>
              )}
            </div>
            {/* Down arrow button (if needs expand) */}
            {needsExpandCollapse && (
              <span
                style={{
                  backgroundColor: "#E9ECEF",
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  flexShrink: 0,
                  height: "25px",
                  width: "20px",
                  boxSizing: "border-box",
                  marginRight: "0",
                  marginTop: "-2px",
                  marginBottom: "-2px",
                }}
                title="Expand to view all types"
                onClick={toggleExpand}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="#0D6EFD"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            )}
            {/* Pen icon to add more types */}
            <span
              style={{
                backgroundColor: "#E9ECEF",
                padding: "0",
                borderTopRightRadius: "4px",
                borderBottomRightRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
                height: "25px",
                width: "25px",
                boxSizing: "border-box",
                marginRight: "-1px",
                marginTop: "-2px",
                marginBottom: "-2px",
                marginLeft: needsExpandCollapse ? "0" : "auto",
              }}
              title="Add type"
              onClick={() => {
                setAddingTypeToRow(isNewRow ? "new" : editingRowId);
                setTypesModalOpen(true);
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.3782 5.57534L9.89 2.12329L11.039 0.972603C11.3537 0.657534 11.7401 0.5 12.1983 0.5C12.6566 0.5 13.043 0.657534 13.3577 0.972603L14.5067 2.12329C14.8213 2.43836 14.9855 2.81849 14.9991 3.2637C15.0128 3.7089 14.8624 4.08904 14.5477 4.40411L13.3782 5.57534ZM12.1881 6.78767L3.48817 15.5H0V12.0068L8.69991 3.29452L12.1881 6.78767Z"
                  fill="#0D6EFD"
                />
              </svg>
            </span>
          </div>
        )}

        {/* Expanded view */}
        {isExpanded && (
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "4px",
              width: "100%",
              maxWidth: "100%",
              background: "#fff",
              padding: "8px",
              boxSizing: "border-box",
            }}
          >
            {/* All chips in expanded view */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "4px",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              {actualTypeIds && actualTypeIds.length > 0 ? (
                actualTypeIds.map((typeId) => (
                  <span
                    key={typeId}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "4px 8px",
                      backgroundColor: "#E9ECEF",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                    }}
                  >
                    {getTypeName(typeId, mappedTypes)}
                    <button
                      type="button"
                      onClick={() => handleRemoveType(typeId, isNewRow)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "2px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#666",
                        marginLeft: "4px",
                        height: "18px",
                        width: "18px",
                        minWidth: "18px",
                      }}
                      title="Remove type"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <line
                          x1="3.5"
                          y1="3.5"
                          x2="10.5"
                          y2="10.5"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        />
                        <line
                          x1="10.5"
                          y1="3.5"
                          x2="3.5"
                          y2="10.5"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </span>
                ))
              ) : (
                <span
                  style={{ color: "#999", fontSize: "13px", paddingTop: "4px" }}
                >
                  Select types
                </span>
              )}
            </div>
            {/* Action buttons row */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Up arrow to collapse */}
              <span
                style={{
                  backgroundColor: "#E9ECEF",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: "12px",
                  color: "#0D6EFD",
                }}
                title="Collapse"
                onClick={toggleExpand}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "4px" }}
                >
                  <path
                    d="M3 7.5L6 4.5L9 7.5"
                    stroke="#0D6EFD"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Collapse
              </span>
              {/* Pen icon to add more types */}
              <span
                style={{
                  backgroundColor: "#E9ECEF",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: "12px",
                  color: "#0D6EFD",
                }}
                title="Add type"
                onClick={() => {
                  setAddingTypeToRow(isNewRow ? "new" : editingRowId);
                  setTypesModalOpen(true);
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "4px" }}
                >
                  <path
                    d="M13.3782 5.57534L9.89 2.12329L11.039 0.972603C11.3537 0.657534 11.7401 0.5 12.1983 0.5C12.6566 0.5 13.043 0.657534 13.3577 0.972603L14.5067 2.12329C14.8213 2.43836 14.9855 2.81849 14.9991 3.2637C15.0128 3.7089 14.8624 4.08904 14.5477 4.40411L13.3782 5.57534ZM12.1881 6.78767L3.48817 15.5H0V12.0068L8.69991 3.29452L12.1881 6.78767Z"
                    fill="#0D6EFD"
                  />
                </svg>
                Add Type
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const isAnyRowEditing = newRow !== null || editingRowId !== null;

  const contentAfterSearchFilter = (
    <div
      style={{
        textAlign: "right",
        display: "flex",
        gap: "10px",
        justifyContent: "flex-end",
        marginTop: "15px",
      }}
    >
      <ExportCsvBtn
        buttonLabel="Export CSV"
        handleExportCSV={() => {
          if (isAnyRowEditing) {
            snackbar(
              "Please save or cancel the current row before exporting CSV.",
              "error"
            );
            return;
          }
          handleExportCSV();
        }}
        whiteVariant
      />
      <button
        className="appbar-button"
        style={{
          border: "1px solid #0D6EFD",
          backgroundColor: "#0D6EFD",
          color: "#ffffff",
        }}
        onClick={() => {
          if (isAnyRowEditing) {
            snackbar(
              "Please save or cancel the current row before uploading CSV.",
              "error"
            );
            return;
          }
          fileInputRef.current?.click();
        }}
      >
        Upload CSV
      </button>
    </div>
  );

  const paginationLeftContent = (
    <AddBtn
      onClick={() => {
        if (isAnyRowEditing) {
          snackbar(
            "Please save or cancel the current row before adding a new one.",
            "error"
          );
          return;
        }
        handleAddRow();
      }}
    />
  );

  // Pagination handlers
  const handleSetLimit = (value) => {
    // Cancel editing when pagination changes
    cancelEditing();
    // React setState handles both function and direct value updates
    setLocalLimit(value);
  };

  const handleSetOffset = (value) => {
    // Cancel editing when pagination changes
    cancelEditing();
    // React setState handles both function and direct value updates
    setLocalOffset(value);
  };

  return (
    <>
      <div>
        <AppBar
          title="Suggestion Rules"
          buttons={[]}
          showTablesDropdown={false}
          showBlackTitle={true}
        />

        <AppTable
          ref={tableRef}
          columnsData={columnNames}
          showSearchInput={true}
          contentAfterSearchFilter={contentAfterSearchFilter}
          paginate={true}
          pagination={{
            limit: [limit, handleSetLimit],
            offset: [offset, handleSetOffset],
            pagination,
            paginationPosition: {
              position: "right",
              leftContent: paginationLeftContent,
            },
          }}
          extrapadding="0px"
          onFilterSortHandler={handleFilterSort}
          reduxSlice={reduxSlice}
          enableLocalStoragePersistence={true}
        >
          {loading ? (
            <tr>
              <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
                Loading...
              </td>
            </tr>
          ) : (
            <>
              {/* New Row */}
              {newRow && (
                <tr>
                  <td>
                    <input
                      type="text"
                      style={{
                        height: "25px",
                        fontSize: "13px",
                        width: "100%",
                        padding: "4px 8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                      }}
                      value={newRowData.rule_name}
                      onChange={(e) =>
                        setNewRowData({
                          ...newRowData,
                          rule_name: e.target.value,
                        })
                      }
                      placeholder="Enter rule name"
                    />
                  </td>
                  <td>
                    {renderApplicableTypes(
                      newRowData.type_ids,
                      true,
                      true,
                      "new"
                    )}
                  </td>
                  <td>
                    <input
                      type="text"
                      style={{
                        height: "25px",
                        fontSize: "13px",
                        width: "100%",
                        padding: "4px 8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                      }}
                      value={newRowData.criteria}
                      onChange={(e) =>
                        setNewRowData({
                          ...newRowData,
                          criteria: e.target.value,
                        })
                      }
                      placeholder="Enter criteria"
                    />
                  </td>
                  <td>
                    <CustomSelect
                      value={newRowData.criteria_format_ref || ""}
                      onChange={(value) =>
                        setNewRowData({
                          ...newRowData,
                          criteria_format_ref: value,
                        })
                      }
                      options={
                        criteriaFormats && criteriaFormats.length > 0
                          ? criteriaFormats.map((format) => {
                              // Handle different possible response structures
                              const formatId =
                                format.criteria_format_id ||
                                format.id ||
                                format.value;
                              const formatName =
                                format.criteria_format_name ||
                                format.name ||
                                format.label;
                              return {
                                value: formatId,
                                label: formatName,
                              };
                            })
                          : [{ value: "", label: "Loading formats..." }]
                      }
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <span
                      style={{
                        color: "#666",
                        fontSize: "13px",
                      }}
                    >
                      -
                    </span>
                  </td>
                  <td>
                    <TableActionGroup
                      mode="editing"
                      onSave={handleSaveNewRow}
                      onCancel={() => {
                        setNewRow(null);
                        setNewRowData({
                          rule_name: "",
                          type_ids: [],
                          criteria: "",
                          criteria_format_ref: "",
                        });
                      }}
                    />
                  </td>
                </tr>
              )}

              {/* Existing Rows */}
              {suggestions && suggestions.length > 0 ? (
                suggestions.map((rule) => {
                  const isEditing = editingRowId === (rule.rule_id || rule.id);
                  // Ensure we preserve existing editedRowData values, especially criteria_format_ref
                  // Use editedRowData if it exists and has been set, otherwise use the rule's data
                  const ruleData = isEditing
                    ? {
                        rule_name:
                          editedRowData.rule_name ?? rule.rule_name ?? "",
                        type_ids:
                          editedRowData.type_ids ??
                          rule.type_ids ??
                          rule.mapped_types?.map((t) => t.type_id) ??
                          [],
                        criteria: editedRowData.criteria ?? rule.criteria ?? "",
                        // Preserve criteria_format_ref from editedRowData if it exists (even if empty string),
                        // otherwise get it from the rule object
                        criteria_format_ref: editedRowData.hasOwnProperty(
                          "criteria_format_ref"
                        )
                          ? editedRowData.criteria_format_ref
                          : getCriteriaFormatRef(rule),
                      }
                    : rule;

                  return (
                    <tr key={rule.rule_id || rule.id}>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            style={{
                              height: "25px",
                              fontSize: "13px",
                              width: "100%",
                              padding: "4px 8px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                            }}
                            value={ruleData.rule_name || ""}
                            onChange={(e) =>
                              setEditedRowData({
                                ...editedRowData,
                                rule_name: e.target.value,
                              })
                            }
                          />
                        ) : (
                          rule.rule_name || "-"
                        )}
                      </td>
                      <td>
                        {renderApplicableTypes(
                          ruleData.type_ids ||
                            rule.type_ids ||
                            rule.mapped_types?.map((t) => t.type_id) ||
                            [],
                          false,
                          isEditing,
                          rule.rule_id || rule.id,
                          rule.mapped_types || null
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            style={{
                              height: "25px",
                              fontSize: "13px",
                              width: "100%",
                              padding: "4px 8px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                            }}
                            value={ruleData.criteria || ""}
                            onChange={(e) =>
                              setEditedRowData({
                                ...editedRowData,
                                criteria: e.target.value,
                              })
                            }
                          />
                        ) : (
                          rule.criteria || "-"
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <CustomSelect
                            value={ruleData.criteria_format_ref || ""}
                            onChange={(value) =>
                              setEditedRowData({
                                ...editedRowData,
                                criteria_format_ref: value,
                              })
                            }
                            options={
                              criteriaFormats && criteriaFormats.length > 0
                                ? criteriaFormats.map((format) => {
                                    // Handle different possible response structures
                                    const formatId =
                                      format.criteria_format_id ||
                                      format.id ||
                                      format.value;
                                    const formatName =
                                      format.criteria_format_name ||
                                      format.name ||
                                      format.label;
                                    return {
                                      value: formatId,
                                      label: formatName,
                                    };
                                  })
                                : [{ value: "", label: "Loading formats..." }]
                            }
                            style={{ width: "100%" }}
                          />
                        ) : (
                          rule.criteria_format_name || "-"
                        )}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          style={{
                            backgroundColor: "white",
                            color: "#0D6EFD",
                            border: "1px solid #0D6EFD",
                            padding: "4px 12px",
                            borderRadius: "4px",
                            fontSize: "12px",
                          }}
                          onClick={(e) => {
                            // Prevent action if editing any row
                            if (isAnyRowEditing) {
                              e.preventDefault();
                              e.stopPropagation();
                              snackbar(
                                "Please save or cancel the current row before viewing matches.",
                                "error"
                              );
                              return;
                            }
                            // Only proceed if match count is greater than 0
                            if ((parseInt(rule.match_count) || 0) === 0) {
                              snackbar(
                                "No matches available for this rule.",
                                "error"
                              );
                              return;
                            }
                            setSelectedRuleForMatches(rule);
                            // Build query string with criteria_format_name and suggestion_rule_id
                            // Note: Don't include offset/limit here as AppTable manages pagination
                            const queryParams = new URLSearchParams({
                              criteria_format_name:
                                rule.criteria_format_name || "",
                              suggestion_rule_id: rule.rule_id || rule.id || "",
                            }).toString();
                            setPartsQueryParams(queryParams);
                            setPartsModalOpen(true);
                          }}
                        >
                          <span>{parseInt(rule.match_count) || 0} matches</span>
                          <FontAwesomeIcon
                            icon={faArrowRight}
                            style={{ marginLeft: "8px", fontSize: "14px" }}
                          />
                        </button>
                      </td>
                      <td>
                        {isEditing ? (
                          <TableActionGroup
                            mode="editing"
                            onSave={() =>
                              handleSaveEdit(rule.rule_id || rule.id)
                            }
                            onCancel={() => {
                              setEditingRowId(null);
                              setEditedRowData({});
                            }}
                          />
                        ) : (
                          <TableActionGroup
                            mode="view"
                            showEdit={true}
                            showDelete={true}
                            onEdit={() => {
                              // Check if a row is already being edited
                              if (isAnyRowEditing) {
                                snackbar(
                                  "Please save or cancel the current row before adding or editing another one.",
                                  "error"
                                );
                                return;
                              }
                              handleEdit(rule);
                            }}
                            onDelete={() => {
                              // Check if a row is already being edited
                              if (isAnyRowEditing) {
                                snackbar(
                                  "Please save or cancel the current row before adding or editing another one.",
                                  "error"
                                );
                                return;
                              }
                              handleDelete(rule.rule_id || rule.id);
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No records found
                  </td>
                </tr>
              )}
            </>
          )}
        </AppTable>
      </div>

      {typesModalOpen && (
        <TypesPopup
          closeTypesModal={() => {
            setTypesModalOpen(false);
            setAddingTypeToRow(null);
          }}
          onSelectType={handleSelectType}
        />
      )}

      {importedInfo && (
        <ImportCsvSuccessPopup
          importedInfo={importedInfo}
          setNullInportData={setImportData}
        />
      )}

      {partsModalOpen && (
        <PartsPopup
          setPartsModalOpen={setPartsModalOpen}
          handleSelectPart={() => {}}
          disableFilter={true}
          disableActions={true}
          items_type="suggestion_rule_match"
          customQueryParams={partsQueryParams}
          disableActionButtons={true}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {showLoader && <Loader />}
    </>
  );
};

export default SuggestionRules;
